import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { MaterialReactTable } from "material-react-table";
import { Box } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import TitlePages from "../../../layout/TitlePages";
import Breadcrumbs from "../../../layout/Breadcrumbs";
import Expandible from "../../../layout/HelperDataTable/Expandible";
import { useSubRowsTableConfig } from "../../../constants";
import { dataArt63 } from "../../../data/dataTransparenciaArt63";

const baseUrlPDF = "https://itetlax.org.mx/assets/pdf/transparencia/art63/";
const baseUrlExcel = "https://itetlax.org.mx/assets/excel/transparencia/art63/";

const ExcelLink = ({ baseUrl, year, urls }) => {
  return urls.map((url) => {
    const fullUrl = `${baseUrl}${year}/${url}`;
    const displayText = url.substring(0, url.lastIndexOf("."));
    return (
      <tr className="table-secondary" key={url}>
        <td>{displayText}</td>
        <td>
          <a href={fullUrl} target="_blank" rel="noreferrer">
            <FontAwesomeIcon icon={faFileExcel} className="btn btn-success" />
          </a>
        </td>
      </tr>
    );
  });
};

const PdfLink = ({ baseUrl, year, urls }) => {
  return urls.map((url) => {
    const fullUrl = `${baseUrl}${year}/${url}`;
    const displayText = url.substring(0, url.lastIndexOf("."));
    return (
      <tr className="table-secondary" key={url}>
        <td>{displayText}</td>
        <td>
          <a href={fullUrl} target="_blank" rel="noreferrer">
            <FontAwesomeIcon icon={faFilePdf} className="btn btn-danger" />
          </a>
        </td>
      </tr>
    );
  });
};

const Art63Table = ({ year }) => {
  const data = useMemo(() => {
    return (dataArt63[year] || []).map((item) => {
      const excels = Object.keys(item).filter((key) => key.startsWith("excel"));
      const pdfs = Object.keys(item).filter((key) => key.startsWith("pdf"));
      return { ...item, hasFiles: excels.length > 0 || pdfs.length > 0 };
    });
  }, [year]);

  useEffect(() => {
    document.title = `Artículo 63 ${year}`;
  }, [year]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "fraccion",
        header: "Fracción",
        footer: "Fracción",
        size: 90,
        enableResizing: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "titulo",
        header: "Título",
        footer: "Título",
        size: 200,
        enableResizing: false,
      },
      {
        accessorKey: "cumplimiento",
        header: "Cumplimiento",
        footer: "Cumplimiento",
        size: 100,
        enableResizing: false,
        filterFn: "equals",
        filterSelectOptions: [
          { text: "Aplica", value: "Aplica" },
          { text: "No Aplica", value: "No Aplica" },
        ],
        filterVariant: "select",
      },
    ],
    []
  );

  const rootData = useMemo(() => data.filter((r) => !r.managerId), [data]);

  const renderTransparencia = ({ row }) => {
    const { managerId, fraccion, ...rest } = row.original;
    const excels = Object.keys(rest)
      .filter((key) => key.startsWith("excel"))
      .map((key) => rest[key]);
    const pdfs = Object.keys(rest)
      .filter((key) => key.startsWith("pdf"))
      .map((key) => rest[key]);

    if (excels.length > 0 || pdfs.length > 0) {
      return (
        <Box id="Box">
          <div className="table-responsive">
            <table className="table table-hover table-sm table-bordered table align-middle w-25">
              <thead>
                <tr>
                  <td colSpan={2}>
                    <br />
                    <strong>Fracción {fraccion}</strong>
                    <br />
                    <br />
                  </td>
                </tr>
              </thead>
              <tbody>
                {excels.length > 0 && (
                  <ExcelLink baseUrl={baseUrlExcel} year={year} urls={excels} />
                )}
                {pdfs.length > 0 && (
                  <PdfLink baseUrl={baseUrlPDF} year={year} urls={pdfs} />
                )}
              </tbody>
            </table>
          </div>
        </Box>
      );
    }
    return null;
  };

  const table = useSubRowsTableConfig(rootData, columns, renderTransparencia, {
    getSubRows: (row) => data.filter((r) => r.managerId === row.id),
  });

  return (
    <>
      <Breadcrumbs
        path={[
          { label: "Transparencia", url: "/Transparencia" },
          { label: "Artículo 63 Obligaciones Específicas", url: "/Articulo63" },
          { label: `Artículo 63 ${year}` },
        ]}
      />
      <TitlePages title="Transparencia" subTitle={`Artículo 63 (${year})`} />
      <Expandible />
      <MaterialReactTable table={table} />
    </>
  );
};

export default Art63Table;

Art63Table.propTypes = {
  year: PropTypes.string.isRequired,
};
